import type { TemperatureSeries } from "../../types";
import { seriesKey } from "../../lib/chartUtils";
import type { AnalysisFilters } from "./types";

export type MonthFilterMode = "none" | "monthYear" | "month";
export type MonthFilterOption = { value: string; label: string };
export type SeriesRecord = {
  item: TemperatureSeries;
  key: string;
  shipmentId: string;
  deviceId: string;
  position: string;
  destinationCity: string;
  destinationCountry: string;
  boxIdentifier: string;
  hasExcursion: boolean;
  bounds: { start: Date; end: Date } | null;
};

export const MONTH_LABELS_FR = [
  "janvier",
  "fevrier",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "aout",
  "septembre",
  "octobre",
  "novembre",
  "decembre",
];

export function buildSeriesRecords(
  series: TemperatureSeries[],
  safeRange: { min: number; max: number },
): SeriesRecord[] {
  const min = Number.isFinite(safeRange.min) ? safeRange.min : Number.NEGATIVE_INFINITY;
  const max = Number.isFinite(safeRange.max) ? safeRange.max : Number.POSITIVE_INFINITY;

  return series.map((item) => {
    const key = seriesKey(item);
    const bounds = getSeriesBounds(item);
    const normalizedDeviceId = (item.deviceId ?? "").trim().toLowerCase();
    const formattedDeviceId = formatDeviceIdentifier(item.shipmentId, item.deviceId).toLowerCase();

    return {
      item,
      key,
      shipmentId: item.shipmentId.toLowerCase(),
      deviceId: `${normalizedDeviceId} ${formattedDeviceId}`.trim(),
      position: (item.position ?? "").toLowerCase(),
      destinationCity: (item.destinationCity ?? "").toLowerCase(),
      destinationCountry: (item.destinationCountry ?? "").toLowerCase(),
      boxIdentifier: (item.boxIdentifier ?? "").toLowerCase(),
      hasExcursion: hasExcursion(item, min, max),
      bounds,
    };
  });
}

export function filterRecords(
  records: SeriesRecord[],
  filters: AnalysisFilters,
  showCityColumn: boolean,
  showBoxesColumn: boolean,
) {
  const normalised = normaliseFilters(filters);
  return records.filter((record) => {
    if (!matches(record.shipmentId, normalised.shipmentId)) return false;
    if (!matches(record.deviceId, normalised.deviceId)) return false;
    if (!matches(record.position, normalised.position)) return false;
    if (showCityColumn && !matches(record.destinationCity, normalised.destinationCity)) return false;
    if (!matches(record.destinationCountry, normalised.destinationCountry)) return false;
    if (showBoxesColumn && !matches(record.boxIdentifier, normalised.boxIdentifier)) return false;
    return true;
  });
}

export function filterRecordsByShipmentMonth(
  records: SeriesRecord[],
  mode: MonthFilterMode,
  selectedValues: string[],
): SeriesRecord[] {
  if (mode === "none" || selectedValues.length === 0) {
    return records;
  }

  const selected = new Set(selectedValues);
  return records.filter((record) => {
    if (!record.bounds) return false;
    return selected.has(toMonthFilterKey(record.bounds.start, mode));
  });
}

export function filterRecordsByExcursion(records: SeriesRecord[], onlyExcursions: boolean) {
  if (!onlyExcursions) {
    return records;
  }

  return records.filter((record) => record.hasExcursion);
}

export function buildMonthFilterOptions(records: SeriesRecord[], mode: MonthFilterMode): MonthFilterOption[] {
  if (mode === "none") return [];

  const values = new Set<string>();
  for (const record of records) {
    if (!record.bounds) continue;
    values.add(toMonthFilterKey(record.bounds.start, mode));
  }

  return buildMonthFilterOptionsFromValues(Array.from(values), mode);
}

export function buildMonthFilterOptionsFromValues(values: string[], mode: MonthFilterMode): MonthFilterOption[] {
  if (mode === "none") return [];
  const uniqueValues = Array.from(new Set(values));

  if (mode === "month") {
    return uniqueValues
      .sort((a, b) => Number(a) - Number(b))
      .map((value) => {
        const monthIndex = Number(value) - 1;
        return {
          value,
          label: monthIndex >= 0 && monthIndex < MONTH_LABELS_FR.length ? MONTH_LABELS_FR[monthIndex] : value,
        };
      });
  }

  return uniqueValues
    .sort((a, b) => a.localeCompare(b))
    .map((value) => {
      const [year, month] = value.split("-");
      const monthIndex = Number(month) - 1;
      const monthLabel = monthIndex >= 0 && monthIndex < MONTH_LABELS_FR.length ? MONTH_LABELS_FR[monthIndex] : month;
      return { value, label: `${monthLabel} ${year}` };
    });
}

export function getSeriesBounds(item: TemperatureSeries): { start: Date; end: Date } | null {
  let startTime = Number.POSITIVE_INFINITY;
  let endTime = Number.NEGATIVE_INFINITY;

  for (const point of item.points) {
    if (!point.timestamp) continue;
    const time = Date.parse(point.timestamp);
    if (!Number.isFinite(time)) continue;
    if (time < startTime) startTime = time;
    if (time > endTime) endTime = time;
  }

  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return null;
  return {
    start: new Date(startTime),
    end: new Date(endTime),
  };
}

export function normaliseFilters(filters: AnalysisFilters): AnalysisFilters {
  return {
    shipmentId: filters.shipmentId.trim().toLowerCase(),
    deviceId: filters.deviceId.trim().toLowerCase(),
    position: filters.position.trim().toLowerCase(),
    destinationCity: filters.destinationCity.trim().toLowerCase(),
    destinationCountry: filters.destinationCountry.trim().toLowerCase(),
    boxIdentifier: filters.boxIdentifier.trim().toLowerCase(),
  };
}

export function toMonthFilterKey(date: Date, mode: MonthFilterMode): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  if (mode === "month") return month;
  return `${date.getUTCFullYear()}-${month}`;
}

function hasExcursion(item: TemperatureSeries, min: number, max: number) {
  return item.points.some((point) => Number.isFinite(point.value) && (point.value < min || point.value > max));
}

function matches(value: string, query: string) {
  if (!query) return true;
  return value.includes(query);
}

function formatDeviceIdentifier(shipmentId: string, rawDeviceId: string | null | undefined): string {
  const normalized = (rawDeviceId ?? "").trim();
  if (!normalized) return "";
  const prefix = `${shipmentId}-`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length) || normalized;
  }
  return normalized;
}
